import { useEffect, useState, useCallback, useRef, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Error Dialog globale
import ErrorDialog from "@/components/ErrorDialog";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
  </div>
);

// ============================================
// LAZY LOADING - Code Splitting per ridurre memoria
// ============================================

// Landing (caricata subito - è la prima pagina)
import LandingPage from "@/pages/LandingPage";

// Pages Admin - LAZY (caricate solo quando servono)
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CondominiumsPage = lazy(() => import("@/pages/CondominiumsPage"));
const CondominiumDetail = lazy(() => import("@/pages/CondominiumDetail"));
const UsersPage = lazy(() => import("@/libs/users"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const RisorsePage = lazy(() => import("@/pages/RisorsePage"));
const ConfigurazionePage = lazy(() => import("@/pages/ConfigurazionePage"));

// Pages Demo/Mockup - LAZY (raramente usate)
const FormPreviewDemo = lazy(() => import("@/pages/FormPreviewDemo"));
const XmlFormDemo = lazy(() => import("@/pages/XmlFormDemo"));
const ConsensoInformatoMockup = lazy(() => import("@/pages/mockups/ConsensoInformatoMockup"));

// Utilities SANDBOX - LAZY
const UICatastoPage = lazy(() => import("@/utilities/UICatasto").then(m => ({ default: m.UICatastoPage })));
const PlanimetriePage = lazy(() => import("@/utilities/Planimetrie").then(m => ({ default: m.PlanimetriePage })));
const CampagnaMisuraPage = lazy(() => import("@/utilities/CampagnaMisura/CampagnaMisuraPage"));
const BolletteAceaUtility = lazy(() => import("@/components/utilities/BolletteAceaUtility"));
const CoefficientiRiduzioneSection = lazy(() => import("@/components/utilities/CoefficientiRiduzioneSection"));
const EstrattoContoPage = lazy(() => import("@/utilities/EstrattoContoBank").then(m => ({ default: m.EstrattoContoPage })));

// Valutazione OMI - Modulo ISOLATO (LOAD/UNLOAD)
const ValutazioneOMIPage = lazy(() => import("@/utilities/ValutazioneOMI").then(m => ({ default: m.ValutazioneOMIPage })));

// Millesimali - Modulo ISOLATO (LOAD/UNLOAD)
const MillesimaliPage = lazy(() => import("@/libs/millesimali").then(m => ({ default: m.MillesimaliPage })));

// Modules Condòmino - LAZY (caricati solo per utenti condomino)
const DashboardCondomino = lazy(() => import("@/modules/condomino").then(m => ({ default: m.DashboardCondomino })));
const CondominiumPageCondomino = lazy(() => import("@/modules/condomino").then(m => ({ default: m.CondominiumPageCondomino })));

// Modules Inquilino - LAZY (caricati solo per utenti inquilino)
const DashboardInquilino = lazy(() => import("@/modules/inquilino").then(m => ({ default: m.DashboardInquilino })));
const CondominiumPageInquilino = lazy(() => import("@/modules/inquilino").then(m => ({ default: m.CondominiumPageInquilino })));

// Form Pubblico Lettura Contatore (accessibile senza auth)
const FormLetturaPublic = lazy(() => import("@/pages/FormLetturaPublic"));

// Tabelle Pubbliche - Accesso read-only con OTP fisso (accessibile senza auth)
const TabellePubliche = lazy(() => import("@/pages/TabellePubliche"));

// Pagine Pubbliche Comunicazioni OTP (accessibili senza auth)
const ComunicazioneConfermaPage = lazy(() => import("@/pages/public/ComunicazioneConfermaPage"));
const ComunicazioneUploadPage = lazy(() => import("@/pages/public/ComunicazioneUploadPage"));

// Auto-detect environment: produzione vs preview
import { BACKEND_URL, API } from '@/config/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper function to get the correct dashboard path based on role and available_dashboards
const getDashboardPath = (role, availableDashboards = []) => {
  // Admin/Assistente always go to /condominiums (main operational view)
  if (role === "amministratore" || role === "assistente") {
    return "/condominiums";
  }
  
  // For condomino role, check available_dashboards
  if (role === "condomino" && availableDashboards.length > 0) {
    // If only inquilino dashboards available
    if (availableDashboards.length === 1 && availableDashboards.includes("inquilino")) {
      return "/inquilino";
    }
    // If only condomino dashboards available, or mix (default to condomino)
    if (availableDashboards.includes("condomino")) {
      return "/condomino";
    }
    // Fallback to inquilino if it's the only option
    if (availableDashboards.includes("inquilino")) {
      return "/inquilino";
    }
  }
  
  // Default fallback based on role
  switch (role) {
    case "inquilino":
      return "/inquilino";
    case "condomino":
      return "/condomino";
    default:
      return "/admin";
  }
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  // Funzione RESET completo
  const fullReset = () => {
    // Cancella tutti i cookie
    document.cookie.split(";").forEach(c => {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    // Cancella storage
    localStorage.clear();
    sessionStorage.clear();
    // Logout context
    if (logout) logout();
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Get session_id from URL hash or query params
      const hash = window.location.hash;
      const search = window.location.search;
      
      let sessionId = null;
      
      // Try hash first (standard OAuth flow)
      const hashMatch = hash.match(/session_id=([^&]+)/);
      if (hashMatch) {
        sessionId = hashMatch[1];
      }
      
      // Try query params as fallback
      if (!sessionId) {
        const params = new URLSearchParams(search);
        sessionId = params.get('session_id');
      }
      
      console.log('[AuthCallback] Processing OAuth callback, session_id:', sessionId ? 'present' : 'missing');
      
      if (sessionId) {
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          console.log('[AuthCallback] Session created successfully for:', response.data.email);
          
          login(response.data);
          toast.success(`Benvenuto, ${response.data.name}!`);
          
          // Clear the hash/query to prevent re-processing
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Redirect based on role and available_dashboards
          const dashboardPath = getDashboardPath(response.data.role, response.data.available_dashboards);
          navigate(dashboardPath, { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error("[AuthCallback] Auth error:", error.response?.data || error.message);
          const errorDetail = error.response?.data?.detail || "Errore di autenticazione";
          
          // RESET completo su qualsiasi errore
          fullReset();
          
          setError(errorDetail);
          toast.error("Errore di autenticazione. Sessione ripulita.");
          
          // Clear URL and redirect home after showing error
          window.history.replaceState({}, document.title, "/");
          setTimeout(() => {
            window.location.href = "/";
          }, 2500);
        }
      } else {
        console.log('[AuthCallback] No session_id found, redirecting to home');
        fullReset();
        window.location.href = "/";
      }
    };

    processAuth();
  }, [navigate, login, logout]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        {error ? (
          <>
            <div className="text-red-500 text-4xl mb-4">✕</div>
            <p className="text-red-600 font-medium">{error}</p>
            <p className="text-slate-500 text-sm mt-2">Reindirizzamento alla home...</p>
          </>
        ) : (
          <>
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-slate-600">Autenticazione in corso...</p>
          </>
        )}
      </div>
    </div>
  );
};

// Protected Route Component with role-based redirect
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to the appropriate dashboard for the user's role
    const dashboardPath = getDashboardPath(user.role, user.available_dashboards);
    return <Navigate to={dashboardPath} replace />;
  }

  return children;
};

// Smart Dashboard Redirect - redirects to role-appropriate dashboard
const SmartDashboardRedirect = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // IMPORTANT: Check if this is an OAuth callback with session_id in hash
  // This can happen when redirected from Google OAuth
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  const dashboardPath = getDashboardPath(user.role, user.available_dashboards);
  return <Navigate to={dashboardPath} replace />;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Route */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Form Demo */}
      <Route path="/form-demo" element={<FormPreviewDemo />} />
      <Route path="/xml-form-demo" element={<XmlFormDemo />} />
      
      {/* Public Form Lettura Contatore (no auth required) */}
      <Route path="/lettura/:token" element={<FormLetturaPublic />} />
      
      {/* Public Comunicazioni OTP Pages (no auth required) - Landing fisse con query param */}
      <Route path="/comunicazione/conferma" element={<ComunicazioneConfermaPage />} />
      <Route path="/comunicazione/upload" element={<ComunicazioneUploadPage />} />
      
      {/* Tabelle Pubbliche - Accesso read-only con Codice+PIN */}
      <Route path="/tabelle-pubbliche" element={<TabellePubliche />} />
      
      {/* Smart redirect for /dashboard */}
      <Route path="/dashboard" element={<SmartDashboardRedirect />} />
      
      {/* ========== AMMINISTRATORE / ASSISTENTE ROUTES ========== */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["amministratore", "assistente"]}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/condominiums"
        element={
          <ProtectedRoute allowedRoles={["amministratore", "assistente"]}>
            <CondominiumsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/condominiums/:id"
        element={
          <ProtectedRoute allowedRoles={["amministratore", "assistente"]}>
            <CondominiumDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/risorse"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <RisorsePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configurazione"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <ConfigurazionePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={["amministratore", "assistente"]}>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      
      {/* ========== UTILITIES ROUTES (SANDBOX ISOLATO) ========== */}
      <Route
        path="/utilities/ui-catasto"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <UICatastoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/utilities/planimetrie"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <PlanimetriePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/utilities/campagna-misura"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <CampagnaMisuraPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/utilities/bollette-acea"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <BolletteAceaUtility />
          </ProtectedRoute>
        }
      />
      <Route
        path="/utilities/coefficienti-riduzione"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <div className="p-6 max-w-5xl mx-auto">
              <CoefficientiRiduzioneSection />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/utilities/millesimali"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <MillesimaliPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/utilities/estratto-conto"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <EstrattoContoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/utilities/valutazione-omi"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <ValutazioneOMIPage />
          </ProtectedRoute>
        }
      />
      
      {/* ========== MOCKUPS (SOLO SVILUPPO) ========== */}
      <Route
        path="/mockup/consenso-informato"
        element={
          <ProtectedRoute allowedRoles={["amministratore"]}>
            <ConsensoInformatoMockup />
          </ProtectedRoute>
        }
      />
      
      {/* ========== CONDOMINO ROUTES ========== */}
      <Route
        path="/condomino"
        element={
          <ProtectedRoute allowedRoles={["condomino", "amministratore"]}>
            <DashboardCondomino />
          </ProtectedRoute>
        }
      />
      <Route
        path="/condomino/condominium/:id"
        element={
          <ProtectedRoute allowedRoles={["condomino", "amministratore"]}>
            <CondominiumPageCondomino />
          </ProtectedRoute>
        }
      />
      
      {/* ========== INQUILINO ROUTES ========== */}
      <Route
        path="/inquilino"
        element={
          <ProtectedRoute allowedRoles={["inquilino", "condomino", "amministratore"]}>
            <DashboardInquilino />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inquilino/condominium/:id"
        element={
          <ProtectedRoute allowedRoles={["inquilino", "condomino", "amministratore"]}>
            <CondominiumPageInquilino />
          </ProtectedRoute>
        }
      />
      
      {/* ========== FORNITORE ROUTES ========== */}
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
      <ErrorDialog />
    </div>
  );
}

export default App;

// Export API URL for use in other components
export { API };
